import { useMemo } from 'react';

import { CustomSelect2, ICustomSelect2Option } from './Selects';

const EVERYTHING_FILTER_VALUE = 'all';
const NONE_FILTER_VALUE = 'none';
const GROUP_ALL_FILTER_VALUE = 'group-all';

interface IExclusiveMultiFilterSelectProps {
    noneLabel: string;
    groupAllLabel: string;
    items: { id: string; name: string }[];
    includeNone: boolean;
    selectedIds: string[];
    disabled: boolean;
    onChange: (next: { includeNone: boolean; selectedIds: string[] }) => void;
};

export const ExclusiveMultiFilterSelect = (props: IExclusiveMultiFilterSelectProps) => {
    const { noneLabel, groupAllLabel, items, includeNone, selectedIds, disabled, onChange } = props;

    const allIds = useMemo(() => items.map(item => item.id), [items]);
    const isGroupAllSelected = !!allIds.length && allIds.every(id => selectedIds.includes(id));
    const isNoneOnlySelected = includeNone && !selectedIds.length;
    const isEverythingSelected = includeNone && (isGroupAllSelected || !allIds.length);

    const options = useMemo<ICustomSelect2Option[]>(() => {
        return [
            {
                value: EVERYTHING_FILTER_VALUE,
                label: 'All',
                section: 'primary',
                checked: isEverythingSelected,
            },
            {
                value: NONE_FILTER_VALUE,
                label: noneLabel,
                section: 'primary',
                checked: includeNone,
            },
            {
                value: GROUP_ALL_FILTER_VALUE,
                label: groupAllLabel,
                section: 'primary',
                checked: isGroupAllSelected,
            },
            ...items.map(item => ({
                value: item.id,
                label: item.name,
                section: 'default' as const,
                checked: selectedIds.includes(item.id),
            })),
        ];
    }, [noneLabel, groupAllLabel, items, selectedIds, includeNone, isGroupAllSelected, isEverythingSelected]);

    const selectedLabel = useMemo(() => {
        if (isEverythingSelected) {
            return 'All';
        };

        if (isNoneOnlySelected) {
            return noneLabel;
        };

        if (isGroupAllSelected) {
            return groupAllLabel;
        };

        if (selectedIds.length === 1) {
            return items.find(item => item.id === selectedIds[0])?.name ?? groupAllLabel;
        };

        return selectedIds.length ? `${selectedIds.length} ${groupAllLabel.replace(/^All\s+/, '')}` : noneLabel;
    }, [isEverythingSelected, isNoneOnlySelected, isGroupAllSelected, noneLabel, groupAllLabel, selectedIds, items]);

    const handleSelect = (option: ICustomSelect2Option) => {
        if (option.value === EVERYTHING_FILTER_VALUE) {
            if (isEverythingSelected) return;
            onChange({ includeNone: true, selectedIds: allIds });
            return;
        };

        if (option.value === NONE_FILTER_VALUE) {
            if (isNoneOnlySelected) return;
            onChange({ includeNone: true, selectedIds: [] });
            return;
        };

        if (option.value === GROUP_ALL_FILTER_VALUE) {
            if (!includeNone && isGroupAllSelected) return;
            onChange({ includeNone: false, selectedIds: allIds });
            return;
        };

        const selectedValue = option.value ?? '';
        const isSelected = selectedIds.includes(selectedValue);
        const nextIds = isSelected
            ? selectedIds.filter(id => id !== selectedValue)
            : [...selectedIds, selectedValue];

        onChange({
            includeNone: nextIds.length === 0,
            selectedIds: nextIds,
        });
    };

    return (
        <CustomSelect2
            selectedLabel={selectedLabel}
            options={options}
            disabled={disabled}
            onSelect={handleSelect}
        />
    );
};
